import React from "react";
import {FormattedMessage} from "react-intl";
import MTDateRangePicker from "@ui-components/MTDateRangePicker";
import {DateRangeFilter} from "../../../utils/pagination/types";
import {fixEndDate, fixStartDate} from "@feature/quotations/quotations-table"

const renderLinkedUser = (sfdcIds, internalUsers) => {
  const idsList = sfdcIds?.split(",") || [];
  if (idsList.length === 0)
    return "-";
  const users = idsList.reduce(
    (acc, curr) => ({
      ...acc,
      [curr]: internalUsers?.find((u) => u.id_salesforce === curr),
    }),
    {},
  );
  return (
    Object.entries(users).map(([sfdcId, user], idx) =>
      <React.Fragment key={idx}>
        <p className="text-xxs">
          {sfdcId}
        </p>
        <p>
          {!!user
            ? user.full_name
            : (
              <span className="text-gray-400">
                <FormattedMessage id="not_available" defaultMessage="Non disponibile" />
              </span>
            )
          }
        </p>
      </React.Fragment>
    )
  )
}

export const getColumns = (intl, internalUsers, rolesLkp, statusesLkp) => [
  {
    title: "ID",
    field: "id",
    editable: "never",
    defaultSort: "desc",
    cellStyle: { fontSize: 12, padding: 0 },
    minWidth: 150,
    type: "numeric",
    pagination: {
      spech: {
        column_name: "id",
        filter_operator: "=",
        value_parser: (value) => parseInt(value),
      }
    }
  },
  {
    title: "E-mail",
    field: "email",
    type: "string",
    /*
    Hubspot ticket 3650435988
    column declaration was missing the pagination.spech object
    needed for the filter to work correctly with paginated api
    call to the backend.
     */
    pagination: {
      spech: {
        column_name: "email",
        filter_operator: "ilike",
        value_parser: (value) => `%${value}%`,
      }
    }
  },
  {
    title: intl.formatMessage({ id: "name" }),
    field: "full_name",
    editable: "never",
    headerStyle: { minWidth: 200, fontWeight: "bold" },
    cellStyle: {
      fontSize: 12,
      padding: 0,
      minWidth: 200,
      textTransform: "uppercase",
      fontWeight: "bold",
    },
    pagination: {
      spech: {
        column_name: ["firstname", "lastname", "company"],
        filter_operator: "ilike",
        value_parser: (value) => `%${value}%`,
      }
    }
  },
  {
    title: intl.formatMessage({ id: "user_type" }),
    field: "role_id",
    lookup: rolesLkp,
    editable: "never",
    pagination: {
      spech: {
        column_name: "role_id",
        filter_operator: "in",
        /*
        Hubspot ticket 3650435988
        the pagination was also wrong for this column
         */
        value_parser: (value) => {
          return value.map((v) => parseInt(v));
        },
      }
    },
  },
  {
    title: intl.formatMessage({ id: "flg_internal" }),
    field: "flg_internal",
    editable: "never",
    type: "boolean",
    /*
    Hubspot ticket 3650435988
    column declaration was missing the pagination.spech object
     */
    pagination: {
      spech: {
        column_name: "flg_internal",
        filter_operator: "=",
        value_parser: (value) => value === 'checked',
      }
    }
  },
  {
    title: intl.formatMessage({ id: "flg_active" }),
    field: "flg_active",
    type: "boolean",
    /*
    Hubspot ticket 3650435988
    column declaration was missing the pagination.spech object
     */
    pagination: {
      spech: {
        column_name: "flg_active",
        filter_operator: "=",
        value_parser: (value) => value === 'checked',
      }
    }
  },
  {
    title: intl.formatMessage({ id: "status" }),
    field: "status",
    lookup: statusesLkp,
    editable: "never",
    /*
    Hubspot ticket 3650435988
    column declaration was missing the pagination.spech object
     */
    pagination: {
      spech: {
        column_name: "status",
        filter_operator: "in",
        value_parser: (value) => value,
      }
    }
  },
  {
    title: intl.formatMessage({ id: "salesforce_id" }),
    field: "id_salesforce",
    type: "string",
    /*
    Hubspot ticket 3650435988
    column declaration was missing the pagination.spech object
     */
    pagination: {
      spech: {
        column_name: "id_salesforce",
        filter_operator: "ilike",
        value_parser: (value) => `%${value}%`,
      }
    }
  },
  // {
  //   title: intl.formatMessage({ id: "Field" }),
  //   field: "id_field",
  //   editable: "never",
  //   filtering: false,
  //   render: ({ id_field }) => renderLinkedUser(id_field, internalUsers),
  // },
  {
    title: intl.formatMessage({ id: "agente_ed" }),
    field: "id_agente_ed",
    editable: "never",
    filtering: false,
    render: ({ agente_ed }) => renderLinkedUser(agente_ed, internalUsers),
  },
  {
    title: intl.formatMessage({ id: "agente_pos" }),
    field: "full_agente_pos",
    editable: "never",
    filtering: false,
    render: ({ agente_pos }) => renderLinkedUser(agente_pos, internalUsers),
  },
  {
    title: intl.formatMessage({ id: "created_at" }),
    editable: "never",
    field: "created_at",
    type: "datetime",
    //filtering: false,
    render: ({ created_at }) => (
      <span>
        {intl.formatDate(created_at, {
          day: "numeric",
          month: "short",
          year: "numeric",
        })}
      </span>
    ),
    /*
    Hubspot ticket 3650435988
    column was filterable but the filter was the default one of
    material table which is horrible, so I added a custom filter
     */
    minWidth: 300,
    filterComponent: (props) => <MTDateRangePicker
      initialStartDate={undefined}
      initialEndDate={undefined}
      onChangeStartDate={() => {
      }}
      onChangeEndDate={() => {
      }}
      onFilterChanged={props.onFilterChanged}
      columnDef={props.columnDef}
      stacked
    />,
    pagination: {
      spech: {
        column_name: "created_at",
        value_parser: (value: DateRangeFilter) => {
          const {startDate, endDate} = value;
          if (startDate && endDate) {
            return [fixStartDate(startDate), fixEndDate(endDate)];
          }
          if (startDate && !endDate) {
            return fixStartDate(startDate)
          }
          if (endDate && !startDate) {
            return fixEndDate(endDate)
          }
          return null
        },
        date_range_filter: true,
      }
    },
    align: "center",
  },
];

export const OPTIONS = {
  filtering: true,
  pageSize: 10,
  searchFieldAlignment: "left",
  searchFieldStyle: {
    marginTop: "2px",
  },
  showTitle: false,
  toolbar: false,
  filterCellStyle: { maxWidth: 250 },
  headerStyle: { whiteSpace: "nowrap", fontWeight: "bold" },
  cellStyle: { fontSize: 12, padding: 0, textTransform: "uppercase" },
};

export const getLocalization = (intl) => ({
  header: {
    actions: "",
  },
  body: {
    editRow: {
      deleteText: intl.formatMessage({ id: "delete_user" }),
    },
  },
  pagination: {
    labelDisplayedRows: "{from}-{to} of {count} users",
    labelRowsSelect: "Users",
  },
});
